import { Box, Typography } from "@mui/material"
import type React from "react"
import { dayjsUTC } from "../../../utils/dates"
import { extraColors } from "../../../utils/theme"
import {
  DEFAULT_GDELT_SORT_MODEL,
  DEFAULT_NWSS_SORT_MODEL,
  PRECOMPUTED_GDELT_TAB_LABEL,
} from "../constants"
import { usePrecomputedBiothreatSignals } from "../PrecomputedBiothreatSignalsContext"
import type { PrecomputedTab } from "../types"
import { usePrecomputedGdeltSignals } from "../usePrecomputedGdeltSignals"
import { usePrecomputedNwssSignals } from "../usePrecomputedNwssSignals"
import { V2_ACCENT, V2_EYEBROW, V2_SURFACE } from "./biothreatV2Tokens"

/** Feed is considered stale once the latest refresh is older than this. */
const FEED_OUTDATED_HOURS = 4
const FEED_OUTDATED_COLOR = "#FF9F45"

const FeedStatus: React.FC = () => {
  const { activeTab } = usePrecomputedBiothreatSignals()
  const { data: gdeltData } = usePrecomputedGdeltSignals({
    page: 0,
    sortModel: DEFAULT_GDELT_SORT_MODEL,
  })
  const { data: nwssData } = usePrecomputedNwssSignals({
    page: 0,
    sortModel: DEFAULT_NWSS_SORT_MODEL,
  })

  const lastUpdated =
    activeTab === "gdelt" ? gdeltData?.last_updated : nwssData?.last_updated
  const lastUpdatedDate =
    typeof lastUpdated === "string" ? dayjsUTC(lastUpdated) : null
  const outdated =
    lastUpdatedDate != null &&
    dayjsUTC().diff(lastUpdatedDate, "hour") >= FEED_OUTDATED_HOURS
  const color = outdated ? FEED_OUTDATED_COLOR : V2_ACCENT.green

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          ...V2_EYEBROW,
          fontSize: "0.8rem",
          color,
        }}
      >
        <Box
          sx={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            backgroundColor: color,
            boxShadow: `0 0 8px ${color}`,
          }}
        />
        {outdated ? "Feed Outdated" : "Feed Live"}
      </Box>
      {lastUpdatedDate ? (
        <Typography
          sx={{
            ...V2_EYEBROW,
            fontSize: "0.75rem",
            color: extraColors.translucent50,
          }}
        >
          Updated {dayjsUTC().to(lastUpdatedDate)}
        </Typography>
      ) : null}
    </Box>
  )
}

const CommandTab: React.FC<{
  label: string
  active: boolean
  onClick: () => void
}> = ({ label, active, onClick }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      ...V2_EYEBROW,
      fontSize: "0.95rem",
      cursor: "pointer",
      background: active ? V2_ACCENT.cyanSoft : "transparent",
      border: `1px solid ${active ? V2_ACCENT.cyan : V2_SURFACE.hairline}`,
      color: active ? extraColors.white : extraColors.translucent50,
      borderRadius: "6px",
      px: 3,
      py: 1.5,
      transition: "all 120ms ease",
      "&:hover": { borderColor: V2_ACCENT.cyan, color: extraColors.white },
    }}
  >
    {label}
  </Box>
)

export const BiothreatV2CommandBar: React.FC = () => {
  const { activeTab, setActiveTab } = usePrecomputedBiothreatSignals()

  const selectTab = (tab: PrecomputedTab) => setActiveTab(tab)

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        px: 3,
        py: 2,
        borderBottom: `1px solid ${V2_SURFACE.hairlineStrong}`,
        backgroundColor: V2_SURFACE.panelSolid,
      }}
    >
      <Box sx={{ display: "flex", gap: 1 }}>
        <CommandTab
          label={PRECOMPUTED_GDELT_TAB_LABEL}
          active={activeTab === "gdelt"}
          onClick={() => selectTab("gdelt")}
        />
        <CommandTab
          label="Wastewater"
          active={activeTab === "nwss"}
          onClick={() => selectTab("nwss")}
        />
      </Box>

      {/* ml: auto right-aligns the feed status within the command bar. */}
      <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
        <FeedStatus />
      </Box>
    </Box>
  )
}
